import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import PageTransition from '../components/ui/PageTransition';
import HeroSection from '../components/sections/HeroSection';

const ContactPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Contact | Consiliuma</title>
        <meta name="description" content="Contact Consiliuma. No spam, no data collection. Reach us directly via Telegram for UK or US projects." />
        <meta name="keywords" content="Contact, Web Design, Telegram, Consiliuma, UK, US, No Spam, No Data Collection" />
        <link rel="canonical" href="/contact" />
        <meta property="og:title" content="Contact | Consiliuma" />
        <meta property="og:description" content="Contact Consiliuma. No spam, no data collection. Reach us directly via Telegram for UK or US projects." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk/contact" />
        <meta property="og:image" content="https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Contact | Consiliuma",
            "description": "Contact Consiliuma. No spam, no data collection. Reach us directly via Telegram for UK or US projects.",
            "url": "https://consiliuma.co.uk/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Consiliuma",
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "contactType": "customer support",
                  "areaServed": "GB",
                  "availableLanguage": "English",
                  "url": "https://t.me/ConsiliumaDesign"
                },
                {
                  "@type": "ContactPoint",
                  "contactType": "customer support",
                  "areaServed": "US",
                  "availableLanguage": "English",
                  "url": "https://t.me/ConsiliumaUSDevTeam"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">Contact Consiliuma</h1>
        <HeroSection
          title="Contact Consiliuma"
          subtitle="No spam. No data collection. Just direct, private communication for UK and US clients."
          ctaText="Message on Telegram"
          ctaLink="https://t.me/ConsiliumaDesign"
          backgroundImage="https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg"
        />

        <section className="py-16 md:py-24" aria-labelledby="contact-policy-heading">
          <div className="container-custom max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-md border-l-4 border-primary"
            >
              <h2 id="contact-policy-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Our No Spam, No Data Collection Policy
              </h2>
              <p className="text-xl text-secondary mb-6">
                Your privacy is our priority. We do not use contact forms, do not collect your data, and never send spam.
              </p>
              <p className="text-secondary mb-6">
                For a fast, private response, contact us directly on Telegram:
              </p>
              <ul className="mb-6">
                <li className="mb-2">
                  <span className="font-semibold text-primary">UK:</span>{' '}
                  <a
                    href="https://t.me/ConsiliumaDesign"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    @ConsiliumaDesign
                  </a>
                </li>
                <li>
                  <span className="font-semibold text-primary">US:</span>{' '}
                  <a
                    href="https://t.me/ConsiliumaUSDevTeam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    @ConsiliumaUSDevTeam
                  </a>
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                Prefer email? Reach us at{' '}
                <a href="mailto:hello@consiliuma.co.uk" className="underline text-primary">
                  hello@consiliuma.co.uk
                </a>
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="contact-info-heading">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 id="contact-info-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">
              How We Work
            </h2>
            <p className="text-xl text-secondary mb-8 text-center">
              We believe in open, honest, and direct communication. Whether you’re in the UK or US, you’ll always speak to a real person—no bots, no mailing lists, and no unwanted follow-ups.
            </p>
            <ul className="list-disc list-inside text-secondary mb-8">
              <li>Discuss your project directly with a developer or designer.</li>
              <li>Receive a tailored response—no automated replies.</li>
              <li>All conversations are private and confidential.</li>
              <li>We never share or sell your contact details.</li>
            </ul>
            <div className="text-center">
              <a
                href="https://t.me/ConsiliumaDesign"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg shadow hover:bg-primary-dark transition"
              >
                Message UK Team
              </a>
              <span className="mx-4 text-secondary font-semibold">or</span>
              <a
                href="https://t.me/ConsiliumaUSDevTeam"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg shadow hover:bg-primary-dark transition"
              >
                Message US Team
              </a>
            </div>
          </div>
        </section>
      </main>
    </PageTransition>
  );
};

export default ContactPage;